import { Word } from '@attain-apps/string-builder'

// NOTE: for the sorting algorithm, there is somewhat of a trade-off between run
// time and memory usage. I.e. we could make the algorithm faster by using more
// memory or reduce the memory usage by having the sorting take a little longer.
// The current implementation (as of 3/20/2024) is meant to be a balance of the
// two

/**
 * Can be used to
 * {@link https://en.wikipedia.org/wiki/Topological_sorting topologically sort}
 * a list of items
 */
export class TopoSorter<T> {
  public get items(): readonly T[] {
    return this.#items
  }

  /**
   * Items grouped by topological depth. Each inner array is a "frontier" of
   * items whose dependencies are fully satisfied by earlier levels, meaning
   * the items within a single level can safely be processed in parallel.
   *
   * Only available after {@link sort} has been called.
   */
  public get levels(): readonly (readonly T[])[] {
    return this.#levels
  }

  /**
   * Tracks total number of items that have been added
   */
  private _itemsCount = 0

  /**
   * Tracks items by label
   */
  private readonly _itemsByLabel: Record<string, T[]> = {}

  /**
   * Tracks dependencies between labels
   */
  private readonly _dependencies: Record<string, Set<string>> = {}

  /**
   * Truly private cache of {@link items}
   *
   * Only exists to allow {@link items} to be `readonly`
   */
  #items: T[] = []

  /**
   * Truly private cache of {@link levels}
   *
   * Only exists to allow {@link levels} to be `readonly`
   */
  #levels: T[][] = []

  /**
   * Adds an item to be sorted
   *
   * __NOTE:__ you must call {@link sort} after adding item(s) to ensure
   * {@link items} are sorted
   */
  public add(
    item: T,
    {
      dependencies,
      dependents,
      label = Word.generate(),
    }: TopoSorter.add.Options = {},
  ): this {
    this._registerItem(item, label)
    if (dependencies?.length) {
      this._registerDependencies(label, dependencies)
    }
    if (dependents?.length) {
      for (const dependent of dependents) {
        this._registerDependencies(dependent, [label])
      }
    }
    return this
  }

  /**
   * Sorts {@link items} topologically
   */
  public sort(): this {
    /**
     * These have already been inserted
     */
    const usedLabels = new Set<string>()
    /**
     * These are not ready to insert because they are waiting on their
     * dependencies
     */
    const blockedLabels = new Set<string>()
    /**
     * These are ready to insert (i.e. all of their dependencies have already
     * been inserted)
     */
    const labelsToInsert: string[] = []
    // Separate labels with no dependencies vs those with dependencies
    for (const label of Object.keys(this._itemsByLabel)) {
      if (this._dependencies[label] === undefined) {
        labelsToInsert.push(label)
      } else {
        blockedLabels.add(label)
      }
    }
    // Now we can actually sort — process one full frontier at a time so we
    // can track topological levels for consumers that need parallel grouping
    this.#items = []
    this.#levels = []
    while (labelsToInsert.length > 0) {
      // All labels currently in `labelsToInsert` form the current frontier
      // (their dependencies have all been placed)
      const currentFrontier = labelsToInsert.slice()
      labelsToInsert.length = 0

      const levelItems: T[] = []
      for (const labelToInsert of currentFrontier) {
        const items = this._itemsByLabel[labelToInsert]
        this.#items.push(...items)
        levelItems.push(...items)
        usedLabels.add(labelToInsert)
      }
      this.#levels.push(levelItems)

      // Discover which blocked labels are now unblocked
      for (const blockedLabel of blockedLabels) {
        const dependenciesOfBlockedLabel = this._dependencies[blockedLabel]
        let readyToInsert = true
        for (const dependency of dependenciesOfBlockedLabel) {
          if (!usedLabels.has(dependency)) {
            readyToInsert = false
            break
          }
        }
        if (readyToInsert) {
          blockedLabels.delete(blockedLabel)
          labelsToInsert.push(blockedLabel)
        }
      }
    }
    if (this.#items.length < this._itemsCount) {
      throw new Error(
        `Failed to topologically sort items. This can be caused by circular dependencies between items or a dependency on an item that was never added`,
      )
    }
    return this
  }

  /**
   * Stores an item to be produced in the sorted list
   */
  private _registerItem(item: T, label: string): void {
    this.#items.push(item)
    const itemsForLabel = (this._itemsByLabel[label] ??= [])
    itemsForLabel.push(item)
    this._itemsCount++
  }

  /**
   * Adds the given dependencies to the given label
   */
  private _registerDependencies(
    label: string,
    dependencies: readonly string[],
  ): void {
    const dependenciesSet = (this._dependencies[label] ??= new Set())
    for (const dependency of dependencies) {
      dependenciesSet.add(dependency)
    }
  }
}
export namespace TopoSorter {
  export namespace add {
    export interface Options {
      /**
       * Allows this item to be referenced as a {@link dependencies dependency} or {@link dependents dependent}
       *
       * Multiple items can share the same label
       */
      label?: string
      /**
       * {@link label} associated with item(s) that must appear before this item
       */
      dependencies?: readonly string[]
      /**
       * {@link label} associated with item(s) that must appear after this item
       */
      dependents?: readonly string[]
    }
  }
}
